// The middle of a document open in the Studio's Editor (pen E; "Studio – the
// desk", "The open document: one frame, the middle by kind"), each the view
// that already exists for its kind:
//  - a material being written (or reading its sources): the B2 skeleton
//    (DeskBriefs.tsx);
//  - any other material, planned (nothing written yet) or written (text,
//    lesson, document, quiz): the editor, on the paper (the
//    editor host follows the Editor's front document, editedMaterial.ts; a
//    quiz's questions, options and correct answers are edited there too);
//  - a source: its original, in the source viewer's centre (read-only);
//  - anything else: the file's own facts (the Knowledge bank's file view).
// Video/audio transcripts and image regions are not built yet: a source of
// those kinds shows the source viewer's player or image.

export { StudioDocument };

import type { ReactNode } from "react";
import { fileKindOf } from "../../../components/KnowledgeBank/knowledgeBankModel.js";
import { SourceOriginal } from "../../../components/SourceViewer/SourceOriginal.js";
import type { DeskDocument } from "../../../components/Studio/studioDesk.js";
import { WritingMiddle } from "../../../components/StudioDesk/DeskBriefs.js";
import { docStateOf } from "../../../components/StudioDesk/deskFacts.js";
import { useAppShell } from "../context.js";
import { FileDetail, LoadView, usePortLoad } from "../ResourceDetail.js";
import { EditedDocument } from "./MaterialCentre.js";

const StudioDocument = ({
    doc,
    add,
}: {
    readonly doc: DeskDocument;
    // The + in the writing line's margin (the Create menu).
    readonly add: ReactNode;
}): ReactNode => {
    const { ports, studio } = useAppShell();
    switch (doc.kind) {
        case "material": {
            const reading = studio.notes.get(doc.id)?.type === "reading";
            // A planned page takes the author's own writing (owner, 30 Oct):
            // its blank paper puts the caret in, and what is written ends
            // Planned. Only the assistant's content needs a source.
            const state = docStateOf(doc);
            if (state === "writing" || (state === "planned" && reading))
                return <WritingMiddle reading={reading} />;
            return (
                // A part list belongs to a container, which opens as a
                // binder, never as a document: nothing to open from here.
                <EditedDocument
                    target={undefined}
                    onOpenPart={() => undefined}
                    blankFace="document"
                    add={add}
                />
            );
        }
        case "source":
            return <SourceMiddle id={doc.id} />;
        case "other":
            return (
                <FileDetail
                    id={doc.id}
                    title={doc.title}
                    loadFile={ports.loadFile}
                />
            );
        default:
            return (
                <FileDetail
                    id={doc.id}
                    title={doc.title}
                    loadFile={ports.loadFile}
                />
            );
    }
};

// A source's original, as the source viewer shows it (read-only).
const SourceMiddle = ({ id }: { readonly id: string }): ReactNode => {
    const { ports } = useAppShell();
    const { load, retry } = usePortLoad(ports.loadSource, id);
    return (
        <LoadView
            load={load}
            kind="source"
            onRetry={retry}
            render={(source) => (
                <SourceOriginal
                    fileKind={fileKindOf(
                        source.meta.filename,
                        source.original?.mediaType,
                    )}
                    source={source}
                />
            )}
        />
    );
};
